import { useCallback, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  AppShell,
  Button,
  Header,
  Hint,
  Sidebar,
  SidebarToggleButton,
  useUiPreferences,
  type SidebarItem,
} from '@rammetec/design-system'
import { AiClientProvider, createAiClient, type AiClient } from '@rammetec/app-kit/ai'
import {
  AssistantPanel,
  AssistantProvider,
  useAssistantPanelPlacement,
} from '@rammetec/app-kit/assistant'
import { usePreference } from '@rammetec/app-kit/preferences'
import { rtLogService } from '@rammetec/core'
import { LayoutDashboard, Sparkles } from 'lucide-react'
import {
  useAvailableInstances,
  useAvailableModules,
  useCurrentUser,
  useRemoteCommands,
  useSessionContext,
} from '@vectorhub/application'
import {
  assistantAutoApprovePref,
  type AppModule,
  type AppModuleInstance,
} from '@vectorhub/domain'
import { OrgSelector } from '@/shared/components/OrgSelector'
import { UserMenu, UserMenuFallback } from '@/shared/components/UserMenu'
import { SidebarAppFooter } from '@/shared/shell/SidebarAppFooter'
import { resolveIcon } from '@/app/modules/Icon'
import { useOrgVectorhubAllowedModules } from '@/shared/hooks/useOrgVectorhubAllowedModules'
import { isAiConfigured } from '@/shared/ai/useAiClient'
import { authService } from '@/authService'
import { env } from '@/config/env'
import { AppShortcuts } from './AppShortcuts'
import { VH_ASSISTANT_SYSTEM_PROMPT } from './assistantPrompt'
import {
  buildOpenableModuleOptions,
  createAssistantGlobalTools,
  openableCatalogKey,
} from './assistantGlobalTools'

const ASSISTANT_OPEN_PREF_KEY = 'vh.assistant.open'
const ASSISTANT_PANEL_WIDTH_KEY = 'vh.assistant.panel'
const SIDEBAR_COLLAPSED_PREF_KEY = 'vh.sidebar.collapsed'
/** Same breakpoint AppShell uses for its mobile default (below Tailwind `md`). */
const SIDEBAR_MOBILE_MEDIA_QUERY = '(max-width: 767px)'

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SIDEBAR_MOBILE_MEDIA_QUERY).matches
}

/**
 * Sidebar open/closed persisted in the user preferences (desktop only). On
 * mobile the sidebar always starts collapsed and toggling it is not saved —
 * otherwise opening it once on the phone would reopen it on every desktop load,
 * and vice versa.
 */
function usePersistedSidebarCollapsed(): [boolean, (collapsed: boolean) => void] {
  const uiPreferences = useUiPreferences()
  const [collapsed, setCollapsed] = useState(() =>
    isMobileViewport() ? true : (uiPreferences.getBool(SIDEBAR_COLLAPSED_PREF_KEY) ?? false),
  )
  const setCollapsedPersisted = useCallback(
    (next: boolean) => {
      setCollapsed(next)
      if (!isMobileViewport()) uiPreferences.setBool(SIDEBAR_COLLAPSED_PREF_KEY, next)
    },
    [uiPreferences],
  )
  return [collapsed, setCollapsedPersisted]
}

let warnedMissingAiHost = false

function buildSidebarItems(
  modules: readonly AppModule[],
  instances: readonly AppModuleInstance[],
): SidebarItem[] {
  const modBySlug = new Map(modules.map((m) => [m.slug, m]))

  return [
    {
      kind: 'item',
      id: 'workspace',
      label: 'Área de trabalho',
      href: '/',
      icon: <LayoutDashboard />,
      shortcut: 'Ctrl+Alt+D',
    },
    ...instances.map((inst) => {
      const def = modBySlug.get(inst.slug)
      return {
        kind: 'item' as const,
        id: inst.id,
        label: inst.label ?? def?.label ?? inst.slug,
        href: `/m/${inst.slug}`,
        icon: resolveIcon(def?.icon ?? inst.slug),
      }
    }),
  ]
}

/** One instance per slug (see ModuleEntry) — the active item comes from the path. */
function getActiveItem(
  pathname: string,
  instances: readonly { id: string; slug: string }[],
): string | undefined {
  const slug = /^\/m\/([^/?#]+)/.exec(pathname)?.[1]
  if (slug) return instances.find((i) => i.slug === slug)?.id
  if (pathname === '/' || pathname === '') return 'workspace'
  return undefined
}

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading } = useCurrentUser()
  const { currentOrgId } = useSessionContext()
  const instances = useAvailableInstances()
  const modules = useAvailableModules()
  const { aiContracted } = useOrgVectorhubAllowedModules(currentOrgId)
  const uiPreferences = useUiPreferences()
  const [autoApprovedTools, setAutoApprovedTools] = usePreference(assistantAutoApprovePref)
  useRemoteCommands()

  const isModuleRoute = location.pathname.startsWith('/m/')

  const [assistantOpen, setAssistantOpen] = useState(
    () => uiPreferences.getBool(ASSISTANT_OPEN_PREF_KEY) ?? false,
  )

  const setAssistantOpenPersisted = (open: boolean) => {
    setAssistantOpen(open)
    uiPreferences.setBool(ASSISTANT_OPEN_PREF_KEY, open)
  }

  const orgIdRef = useRef(currentOrgId)
  orgIdRef.current = currentOrgId

  const locationRef = useRef({
    pathname: location.pathname,
    modules,
    instances,
  })
  locationRef.current = {
    pathname: location.pathname,
    modules,
    instances,
  }

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const catalogKey = useMemo(
    () => openableCatalogKey(instances, modules),
    [instances, modules],
  )

  const aiClient = useMemo((): AiClient | null => {
    if (!isAiConfigured()) {
      if (!warnedMissingAiHost) {
        warnedMissingAiHost = true
        rtLogService.warn(
          'Assistente de IA indisponível: VITE_SYSTEM_API_URL não configurada.',
        )
      }
      return null
    }
    return createAiClient({
      baseUrl: env.systemApiUrl!,
      getIdToken: () => authService.getIdToken(),
      getOrganizationId: async () => orgIdRef.current ?? null,
    })
  }, [])

  const globalTools = useMemo(() => {
    const snap = locationRef.current
    const openable = buildOpenableModuleOptions(snap.instances, snap.modules)
    return createAssistantGlobalTools(
      () => locationRef.current,
      (to) => navigateRef.current(to),
      openable,
    )
    // catalogKey is the content fingerprint; locationRef always holds latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogKey])

  const assistantAvailable = aiClient != null

  const shell = (
    <AppLayoutShell
      assistantAvailable={assistantAvailable}
      aiContracted={aiContracted}
      assistantOpen={assistantOpen}
      onAssistantOpenChange={setAssistantOpenPersisted}
      user={user}
      isLoading={isLoading}
      isModuleRoute={isModuleRoute}
      modules={modules}
      instances={instances}
      pathname={location.pathname}
      onNavigate={navigate}
    />
  )

  if (!aiClient) return shell

  return (
    <AiClientProvider client={aiClient}>
      <AssistantProvider
        client={aiClient}
        usePoint="vh.assistant"
        // 5 (default do app-kit) não comporta pedido de duas partes: no SysColect
        // "incluir X e me mostrar no grid" gastava as cinco antes de filtrar.
        // 10 também não: incluir um registro por formulário (abrir, resolver os
        // lookups, preencher) consome isso sozinho. O teto é rede contra laço,
        // não orçamento — o custo é controlado por ROUNDS_PER_TOKEN no servidor.
        maxRounds={30}
        persistConversation
        autoApprovedTools={autoApprovedTools}
        onAutoApprovedToolsChange={(next) => setAutoApprovedTools([...next])}
        systemPrompt={VH_ASSISTANT_SYSTEM_PROMPT}
        globalTools={globalTools}
      >
        {shell}
      </AssistantProvider>
    </AiClientProvider>
  )
}

/** Shell body — reads panel placement inside AssistantProvider when present. */
function AppLayoutShell({
  assistantAvailable,
  aiContracted,
  assistantOpen,
  onAssistantOpenChange,
  user,
  isLoading,
  isModuleRoute,
  modules,
  instances,
  pathname,
  onNavigate,
}: {
  assistantAvailable: boolean
  aiContracted: boolean
  assistantOpen: boolean
  onAssistantOpenChange: (open: boolean) => void
  user: ReturnType<typeof useCurrentUser>['user']
  isLoading: boolean
  isModuleRoute: boolean
  modules: readonly AppModule[]
  instances: readonly AppModuleInstance[]
  pathname: string
  onNavigate: (to: string) => void
}) {
  const panelPlacement = useAssistantPanelPlacement()
  const shellHostsPanel = panelPlacement === 'shell'
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedSidebarCollapsed()
  const sidePanelOpen =
    assistantAvailable && aiContracted && assistantOpen && shellHostsPanel

  return (
    <>
      <AppShortcuts />
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        onSidebarCollapsedChange={setSidebarCollapsed}
        header={
          <Header
            brand={{ id: 'vectorhub', label: 'VectorHub' }}
            logo={<img src="/favicon.svg" alt="VectorHub" className="h-5 w-5" />}
            startSlot={<SidebarToggleButton />}
            endSlot={
              user ? (
                <div className="flex items-center gap-2">
                  {!isModuleRoute && <OrgSelector />}
                  {assistantAvailable && aiContracted && (
                    <Hint label="Assistente de IA" side="bottom">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Assistente de IA"
                        aria-pressed={assistantOpen}
                        onClick={() => onAssistantOpenChange(!assistantOpen)}
                      >
                        <Sparkles />
                      </Button>
                    </Hint>
                  )}
                  <UserMenu user={user} />
                </div>
              ) : isLoading ? (
                <UserMenuFallback />
              ) : null
            }
          />
        }
        sidebar={
          <Sidebar
            searchable
            searchShortcut="Ctrl+Alt+S"
            items={buildSidebarItems(modules, instances)}
            activeId={getActiveItem(pathname, instances)}
            onItemClick={(_, item, e) => {
              if (item.kind !== 'item' || !item.href) return
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
              e.preventDefault()
              onNavigate(item.href)
            }}
            footer={<SidebarAppFooter productLabel="VectorHub - Console" />}
          />
        }
        sidePanel={
          shellHostsPanel && assistantAvailable && user ? (
            <AssistantPanel
              currentUserId={user.userId}
              onCollapse={() => onAssistantOpenChange(false)}
            />
          ) : undefined
        }
        sidePanelOpen={sidePanelOpen}
        sidePanelWidthKey={ASSISTANT_PANEL_WIDTH_KEY}
      >
        <Outlet />
      </AppShell>
    </>
  )
}
